import { Typography, Grid } from '@ori-ui/mui';
import type { GlobalCustomerRegistrationsRegistrationLegalDetailsDto } from '@ori/global-api-client-react';
import type {
  StaticCustomerRegistrationsMetadataRegistrationLegalDetailsMetadataDto,
  StaticCustomersMetadataApprovalPropertyDto,
} from '@ori/static-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { ContentKeys, Localization } from '../../../Localization';
import { defaultTranslations } from '../../../constants/defaultTranslations_simplified-registration-BrandPartner';
import {
  useStepperData,
  useVirtualPageAnalyticsData,
  useFormError,
  useConfiguration,
  GetContent,
} from '../../../contexts';
import { TranslationKey } from '../../../models';
import { CheckboxFormFieldByConfig } from '../../FieldsByConfig';
import { FormValidationSummary } from '../../FormValidationSummary';
import { TermsConditionsCheckbox } from '../../TermsConditionsCheckbox';
import { LegalDetailApprovals } from './LegalDetailApprovals';
import { LegalDocuments } from './LegalDocuments';
import {
  StyledForm,
  BackButtton,
  ConditionTitle,
  ConditionsInfoWrapper,
  DocumentInfoWrapper,
  StyledArrowIcon,
  SubmitButtton,
  StyledLink,
} from './StyledComponentsLegalForm';

interface ILegalDetailsFormProps {
  initialData?: GlobalCustomerRegistrationsRegistrationLegalDetailsDto;
  config?: StaticCustomerRegistrationsMetadataRegistrationLegalDetailsMetadataDto;
  isSuccessSubmit: boolean;
  onSubmitForm: (data: GlobalCustomerRegistrationsRegistrationLegalDetailsDto) => void;
}

export const LegalDetailsForm: FC<ILegalDetailsFormProps> = (props) => {
  const { initialData, config, isSuccessSubmit, onSubmitForm } = props;
  const {
    context: { stepperData },
  } = useStepperData();
  const { activeStep, setActiveStep, stepsOrder } = stepperData;

  const { getTestIdProps } = useTestId();

  const { customerServiceUrl } = useConfiguration();

  const methods = useForm<GlobalCustomerRegistrationsRegistrationLegalDetailsDto>({
    defaultValues: initialData,
    reValidateMode: 'onChange',
    shouldFocusError: true,
    mode: 'all',
  });

  const { watch, formState } = methods;
  const { isValid, isSubmitting } = formState;

  const values = watch();

  const {
    context: { errorData },
  } = useFormError();
  const { customErrors } = errorData;

  const identityErr = Object.keys(values.identityCard ?? {}).map((value) => `identityCard.${value}`);
  const prevValues = Object.keys(values);
  const identityValues = identityErr;
  const newValues = [...prevValues, ...identityValues];

  const invalidCodeErrors = customErrors?.filter(
    (er) => !newValues.map((val) => val.toLowerCase()).includes(er.fieldName?.toLowerCase() ?? ''),
  );

  const availableApprovals =
    config?.approvals &&
    Object.values(config.approvals).filter(
      (approval: StaticCustomersMetadataApprovalPropertyDto) => approval.isAvailable,
    );
  const {
    virtualPageAnalyticsContext: { virtualPageAnalyticsData },
  } = useVirtualPageAnalyticsData();
  const { setPreviousStep } = virtualPageAnalyticsData;

  const handleBack = () => {
    setPreviousStep(stepsOrder[activeStep]);
    setActiveStep(activeStep - 1);
  };

  const isAnyAprrovalAvailable = Boolean(availableApprovals && availableApprovals.length > 0);

  const isAnyDocumentAvailable =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- we need logical operator ||
    config?.identityCard?.isAvailable || config?.additionalIdentityCard?.isAvailable;

  return (
    <FormProvider {...methods}>
      <FormValidationSummary
        invalidPropertyErrors={invalidCodeErrors}
        customerServiceUrl={customerServiceUrl}
      />
      <StyledForm
        noValidate
        onSubmit={methods.handleSubmit(onSubmitForm)}
      >
        {isAnyDocumentAvailable ? (
          <DocumentInfoWrapper>
            <Typography
              variant="h6"
              color="textPrimary"
              textAlign="left"
            >
              <Localization
                id={TranslationKey.ThirdStep_Your_Documents_Header}
                defaultMessage={defaultTranslations.ThirdStep_Your_Documents_Header}
              />
            </Typography>
            <Typography
              variant="body1"
              color="textPrimary"
            >
              <Localization
                id={TranslationKey.ThirdStep_Your_Documents_Text}
                defaultMessage={defaultTranslations.ThirdStep_Your_Documents_Text}
              />
            </Typography>
          </DocumentInfoWrapper>
        ) : null}
        <Grid
          container
          justifyContent="center"
          textAlign="center"
        >
          <Grid
            item
            xs={12}
            sm={12}
          >
            <LegalDocuments documentsConfig={config} />
            <ConditionsInfoWrapper>
              <ConditionTitle
                variant="h6"
                color="textPrimary"
                textAlign="left"
              >
                <Localization
                  id={TranslationKey.ThirdStep_GeneralConditions}
                  defaultMessage={defaultTranslations.ThirdStep_GeneralConditions}
                />
              </ConditionTitle>
              <Typography
                variant="body1"
                color="textPrimary"
                textAlign="left"
              >
                <Localization
                  id={TranslationKey.ThirdStep_GeneralConditions_Text}
                  defaultMessage={defaultTranslations.ThirdStep_GeneralConditions_Text}
                />
              </Typography>
            </ConditionsInfoWrapper>
            {isAnyAprrovalAvailable ? (
              <LegalDetailApprovals
                config={config}
                approvalValues={values}
              />
            ) : null}
            <CheckboxFormFieldByConfig
              name="confirmation1"
              label={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: values.confirmation1 ? 'bold' : 'normal' }}
                >
                  <Localization
                    id={TranslationKey.ThirdStep_Confirmation1}
                    defaultMessage={defaultTranslations.ThirdStep_Confirmation1}
                    values={{
                      LegalDocument1_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument1_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument1_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument1_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument2_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument2_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument2_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument2_Link_Label}
                          />{' '}
                        </StyledLink>
                      ),
                      LegalDocument3_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument3_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument3_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument3_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument4_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument4_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument4_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument4_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument5_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument5_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument5_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument5_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument6_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument6_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument6_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument6_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument7_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument7_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument7_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument7_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument8_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument8_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument8_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument8_Link_Label}
                          />
                        </StyledLink>
                      ),
                    }}
                  />
                  {config?.confirmation1?.validation?.isMandatory ? ' *' : null}
                </Typography>
              }
              value={values.confirmation1}
              {...config?.confirmation1}
            />
            <CheckboxFormFieldByConfig
              name="confirmation2"
              label={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: values.confirmation2 ? 'bold' : 'normal' }}
                >
                  <Localization
                    id={TranslationKey.ThirdStep_Confirmation2}
                    defaultMessage={defaultTranslations.ThirdStep_Confirmation2}
                    values={{
                      LegalDocument1_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument1_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument1_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument1_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument2_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument2_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument2_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument2_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument3_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument3_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument3_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument3_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument4_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument4_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument4_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument4_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument5_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument5_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument5_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument5_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument6_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument6_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument6_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument6_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument7_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument7_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument7_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument7_Link_Label}
                          />
                        </StyledLink>
                      ),
                      LegalDocument8_Link: (
                        <StyledLink
                          href={GetContent(ContentKeys.LegalDocument8_Link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Localization
                            id={TranslationKey.LegalDocument8_Link_Label}
                            defaultMessage={defaultTranslations.LegalDocument8_Link_Label}
                          />
                        </StyledLink>
                      ),
                    }}
                  />
                  {config?.confirmation2?.validation?.isMandatory ? ' *' : null}
                </Typography>
              }
              value={values.confirmation2}
              {...config?.confirmation2}
            />
            <TermsConditionsCheckbox config={config} />
            <SubmitButtton
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isValid || isSubmitting || isSuccessSubmit}
              {...getTestIdProps('submit-btn-legalForm')}
            >
              <Localization
                id={TranslationKey.ThirdStep_Button}
                defaultMessage={defaultTranslations.ThirdStep_Button}
              />
            </SubmitButtton>
          </Grid>
        </Grid>
        <BackButtton
          variant="text"
          color="inherit"
          onClick={() => handleBack()}
        >
          <StyledArrowIcon />
          <Localization
            id={TranslationKey.back}
            defaultMessage={defaultTranslations.back}
          />
        </BackButtton>
      </StyledForm>
    </FormProvider>
  );
};
